import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { memo, useCallback, useContext, useMemo, useState } from 'react';

import Address from '@/components/Address';
import { Color } from '@/constants/color';
import {
  OperationsLogType,
  RewardCardAccountStatus,
  RewardCardStatus
} from '@/constants/rewardCard';
import AppContext from '@/contexts/appContext';
import CardOrderDetails from '@/pages/RewardCardList/components/CardOrderDetails';
import StatusUpdateButton from '@/pages/RewardCardList/components/StatusUpdateButton';
import TerminateButton from '@/pages/RewardCardList/components/TerminateButton';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import ListCard from '@/ui/components/ListCard';
import { currencyFormatter } from '@/utils/currencies';
import { dateFromDateStringWithAppendedTime } from '@/utils/dates';
import { hasPermission, PEOPLE_CHANGE_REWARD_CARDS } from '@/utils/userPermission';

import { Props } from './types';

const RewardCardItem = ({ rewardCardAccount, hasCurrentActiveCard, operationsLogs }: Props) => {
  const [showCardOrderDetails, toggleShowCardOrderDetails] = useState<boolean>(false);
  const { permissions } = useContext(AppContext);
  const canChangeRewardCard = hasPermission({
    permissions,
    requiredPermission: PEOPLE_CHANGE_REWARD_CARDS
  });
  const {
    account_id,
    account_status,
    available_balance_in_cents,
    balance_forwarded_in_cents,
    balance_forwarded_to_account_id,
    billing_address,
    closing_balance_in_cents,
    shipping_address,
    current_card
  } = rewardCardAccount;

  const hasBlockOperation = useMemo(() => {
    return operationsLogs?.some(
      (operation) => operation.operation_type === OperationsLogType.BLOCK
    );
  }, [operationsLogs]);

  const hasUnblockOperation = useMemo(() => {
    return operationsLogs?.some(
      (operation) => operation.operation_type === OperationsLogType.UNBLOCK
    );
  }, [operationsLogs]);

  const hasTerminateOperation = useMemo(() => {
    return operationsLogs?.some(
      (operation) => operation.operation_type === OperationsLogType.TERMINATE
    );
  }, [operationsLogs]);

  const disableActions = !!(hasBlockOperation || hasUnblockOperation || hasTerminateOperation);

  const renderCurrentCard = useCallback(() => {
    if (current_card) {
      return (
        <div>
          {current_card?.card_status && (
            <Badge
              color={
                current_card?.card_status === RewardCardStatus.ACTIVE ? Color.GREEN : Color.RED
              }>
              {current_card.card_status}
            </Badge>
          )}
        </div>
      );
    }

    return null;
  }, [current_card]);

  return (
    <ListCard
      debug={rewardCardAccount}
      headingIcon={faCreditCard}
      title={
        current_card ? `**** ${current_card.last_4_card_number}` : 'No Current Card For Account'
      }
      {...(current_card && {
        subtitle: `Exp: ${dateFromDateStringWithAppendedTime(
          current_card.expiration_date
        ).toLocaleString('en-US', {
          month: '2-digit',
          year: '2-digit'
        })}
      `
      })}
      {...(canChangeRewardCard && {
        headingActions: (
          <div className="space-x-1">
            <StatusUpdateButton
              account={rewardCardAccount}
              disabled={disableActions}
              hasCurrentActiveCard={hasCurrentActiveCard}
            />

            {account_status !== RewardCardAccountStatus.CLOSED && (
              <TerminateButton accountId={account_id} disabled={disableActions} />
            )}
          </div>
        )
      })}>
      {renderCurrentCard()}
      <DataList>
        {current_card && (
          <DataRow label="Issued">
            {dateFromDateStringWithAppendedTime(current_card.issued_date).toLocaleString('en-US', {
              day: 'numeric',
              month: 'numeric',
              year: '2-digit'
            })}
          </DataRow>
        )}
        {current_card &&
          current_card.card_status == RewardCardStatus.ACTIVE &&
          current_card.activated_at && (
            <DataRow label="Activated">
              {dateFromDateStringWithAppendedTime(current_card.activated_at).toLocaleString(
                'en-US',
                {
                  day: 'numeric',
                  month: 'numeric',
                  year: '2-digit'
                }
              )}
            </DataRow>
          )}
        <DataRow label="Available Balance">
          {currencyFormatter(available_balance_in_cents / 100)}
        </DataRow>
        {closing_balance_in_cents && (
          <DataRow label="Closing Balance">
            {currencyFormatter(closing_balance_in_cents / 100)}
          </DataRow>
        )}
        {(balance_forwarded_in_cents || balance_forwarded_to_account_id) && (
          <DataRow label="Balance Forwarded">
            <DataList>
              {balance_forwarded_in_cents && (
                <DataRow label="Amount">
                  {currencyFormatter(balance_forwarded_in_cents / 100)}
                </DataRow>
              )}
              {balance_forwarded_to_account_id && (
                <DataRow label="To">
                  <a
                    className="text-blue-700 underline"
                    href={`/people/${rewardCardAccount.person_id}/reward-card-account/${balance_forwarded_to_account_id}`}>
                    View account
                  </a>
                </DataRow>
              )}
            </DataList>
          </DataRow>
        )}
      </DataList>

      <div className="mt-2">
        {!showCardOrderDetails && (
          <Button
            color={Color.WHITE}
            onClick={() => toggleShowCardOrderDetails((current) => !current)}
            size="xs">
            Show Card Order Details
          </Button>
        )}
        {showCardOrderDetails && (
          <div className="mt-2">
            {rewardCardAccount.order ? (
              <CardOrderDetails cardOrder={rewardCardAccount.order} />
            ) : (
              <div>No card order on account</div>
            )}

            <DataList>
              {shipping_address && (
                <DataRow label="Shipping Address">
                  <Address address={shipping_address} />
                </DataRow>
              )}

              {billing_address && (
                <DataRow label="Billing Address">
                  <Address address={billing_address} />
                </DataRow>
              )}
            </DataList>
          </div>
        )}
      </div>
    </ListCard>
  );
};

export default memo(RewardCardItem);
